<template>
  <div class="brands-marketplace-wrapper">
    <div class="top-portion-wrapper" ref="top-portion">
      <div class="header-wrapper">
        <h1 class="page-header text-center">brands marketplace</h1>
        <div class="links-wrapper">
          <a class="refer-brand" @click="showSimpleModal = true">refer a brand</a>
          <a target="_blank" :href="demoLink">need more info?</a>
          <a href="/logs/sponsorship_requests">sponsorship request log</a>
        </div>
      </div>

      <error-banner />

      <brands-marketplace-display
        v-if="selectedDisplay"
        @removeMarketplaceDisplay="removeMarketplaceDisplay"
        :marketplaceDisplay="marketplaceDisplay"
      />

      <brands-marketplace-navigation
        v-if="marketplaceDataLoaded"
        :showStateNav="showStateNav"
        :showSponsoredNav="showSponsoredNav"
        :showStoreNav="showStoreNav"
        @scrollToCarousel="scrollToElement"
      />
    </div>

    <div class="brand-carousels" ref="brand-carousels">
      <div v-if="stateSlides && marketplaceDataLoaded" class="states-carousels-wrapper" ref="by-state">
        <section class="states-list"
          :class="{ 'default-state': index === 0, 'expanding-all-states': isExpandingAllStates}"
          v-for="(state, index) in stateSlides" :key="index">
          <p v-if="state.slides && marketplaceDataLoaded" class="carousel-title">{{state.name}} cannabis brands</p>
          <carousel :slides="state.slides"
            :swiperOptions="swiperOptions"
            class="pt-3">
            <template #swiper-slide-content="{ slide }">
              <div class="img-container inline-block relative" @click="displayClicked(slide, state.name)">
                <span v-if="slide.isNew" class="new-badge absolute">new</span>
                <img v-if="slide.imgSrc"
                  class="slide-image"
                  :class="{ 'shadow-border' : selectedDisplay === slide }"
                  :src="slide.imgSrc"
                  :alt="slide.imgSrc">
                <p v-if="slide.label" class="display-label text-blue text-center">{{ slide.label }}</p>
              </div>
            </template>
          </carousel>
          <p v-if="index === 0"
            class="initial expand-states"
            :class="{active: isExpandingAllStates}"
            @click="isExpandingAllStates = !isExpandingAllStates">
            <span>{{ !isExpandingAllStates ? '+' : '-' }}</span>
            see all states
          </p>
        </section>
      </div>

      <div v-if="sponsoredSlides && marketplaceDataLoaded" class="previously-sponsored-carousel-wrapper">
        <section class="previously-sponsored" ref="sponsored">
          <p v-if="sponsoredSlides.slides && marketplaceDataLoaded" class="carousel-title">{{sponsoredSlides.name}}</p>
          <carousel :slides="sponsoredSlides.slides"
              :swiperOptions="swiperOptions"
              class="pt-3">
            <template #swiper-slide-content="{ slide }">
              <div class="img-container inline-block relative" @click="displayClicked(slide, sponsoredSlides.name)">
                <span v-if="slide.isNew" class="new-badge absolute">new</span>
                <img v-if="slide.imgSrc"
                  class="slide-image"
                  :class="{ 'shadow-border' : selectedDisplay === slide }"
                  :src="slide.imgSrc"
                  :alt="slide.imgSrc">
                <p v-if="slide.label" class="display-label text-blue text-center">{{ slide.label }}</p>
              </div>
            </template>
          </carousel>
        </section>
      </div>

      <div v-if="storeSlides && marketplaceDataLoaded" class="in-store-carousel-wrapper">
        <section class="in-store" ref="in-store">
          <p v-if="storeSlides.slides && marketplaceDataLoaded" class="carousel-title">{{storeSlides.name}}</p>
          <carousel :slides="storeSlides.slides"
              :swiperOptions="swiperOptions"
              class="pt-3">
            <template #swiper-slide-content="{ slide }">
              <div class="img-container inline-block relative" @click="displayClicked(slide, storeSlides.name)">
                <span v-if="slide.isNew" class="new-badge absolute">new</span>
                <img v-if="slide.imgSrc"
                  class="slide-image"
                  :class="{ 'shadow-border' : selectedDisplay === slide }"
                  :src="slide.imgSrc"
                  :alt="slide.imgSrc">
                <p v-if="slide.label" class="display-label text-blue text-center">{{ slide.label }}</p>
              </div>
            </template>
          </carousel>
        </section>
      </div>
    </div>

    <simple-modal
      v-if="showSimpleModal"
      class="refer-brand-modal"
      title="refer a brand"
      @rejected="closeModalAndResetFields"
      @resolved="validateAndCreateRequest"
    >
      <p class="text-center">enter brand information in the fields below</p>
      <section class="refer-brand-wrapper">
        <label>brand name</label>
        <input
          :class="{ 'refer-brand-error' : !isBrandNameValid }"
          name="brand_name"
          type="text"
          maxlength="100"
          v-model.trim="brandName"
          @blur="validateField('brand_name')">
        <span class="error" v-if="!isBrandNameValid">brand name required</span>
        <label>contact first name</label>
        <input
          :class="{ 'refer-brand-error' : !isFirstNameValid }"
          name="contact_first_name"
          type="text"
          maxlength="100"
          v-model.trim="brandContactFirstName"
          @blur="validateField('contact_first_name')">
        <span class="error" v-if="!isFirstNameValid">contact first name required</span>
        <label>contact last name</label>
        <input
          :class="{ 'refer-brand-error' : !isLastNameValid }"
          name="contact_last_name"
          type="text"
          maxlength="100"
          v-model.trim="brandContactLastName"
          @blur="validateField('contact_last_name')">
        <span class="error" v-if="!isLastNameValid">contact last name required</span>
        <label>phone number</label>
        <input
          :class="{ 'refer-brand-error' : !isPhoneNumberValid }"
          name="phone_number"
          type="tel"
          maxlength="10"
          v-model.trim="brandPhoneNumber"
          @blur="validateField('phone_number')">
        <span class="error" v-if="!isPhoneNumberValid">invalid phone number</span>
        <label>email address</label>
        <input
          :class="{ 'refer-brand-error' : !isEmailValid }"
          name="email"
          type="email"
          v-model.trim="brandEmail"
          @blur="validateField('email')">
        <span class="error" v-if="!isEmailValid">invalid email</span>
      </section>
      <button slot="buttons"
        class="sb-button text-base sm:text-xl py-2 px-8 sm:py-4 sm:px-12"
        @click="validateAndCreateRequest">done</button>
    </simple-modal>
  </div>
</template>

<script>
  import { brandsMarketplaceAPI } from '../../services/api/brandsMarketplaceAPI'
  import { Carousel, SimpleModal, UnexpectedError, ErrorBanner, UserError } from '@springbig/sb-components'
  import { isWithinInterval, subMonths } from 'date-fns'
  import BrandsMarketplaceNavigation from '../../components/BrandsMarketplace/BrandsMarketplaceNavigation'
  import BrandsMarketplaceDisplay from '../../components/BrandsMarketplace/BrandsMarketplaceDisplay'
  import EmailValidator from 'email-validator'

  export default {
    name: 'BrandsMarketplace',

    components: {
      BrandsMarketplaceNavigation,
      BrandsMarketplaceDisplay,
      Carousel,
      SimpleModal,
      ErrorBanner
    },

    data() {
      return {
        brandContactFirstName: '',
        brandContactLastName: '',
        brandEmail: '',
        brandName: '',
        brandPhoneNumber: '',
        isBrandsLoading: null,
        isContactInfoValid: true,
        demoLink: 'http://calendly.com/mkilgo',
        isFirstNameValid: true,
        isLastNameValid: true,
        isBrandNameValid: true,
        isExpandingAllStates: false,
        marketplaceDataLoaded: false,
        marketplaceDisplay: null,
        model: 0,
        previousSelectedRow: null,
        selectedDisplay: null,
        showSimpleModal: false,
        showSponsoredNav: false,
        showStateNav: false,
        showStoreNav: false,
        sponsoredSlides: {},
        stateSlides: [],
        storeSlides: {},
        swiperOptions: {
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          },
          grabCursor: true,
          breakpoints: {
            1650: {
              slidesPerView: 5,
              slidesPerGroup: 5,
              spaceBetween: 10
            },
            1540: {
              slidesPerView: 5,
              spaceBetween: 10,
              slidesPerGroup: 5
            },
            1224: {
              slidesPerView: 5,
              spaceBetween: 10,
              slidesPerGroup: 5
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 10,
              slidesPerGroup: 3
            },
            640: {
              slidesPerView: 2,
              spaceBetween: 10,
              slidesPerGroup: 2
            },
            320: {
              slidesPerView: 1,
              spaceBetween: 10,
              slidesPerGroup: 1
            }
          },
        },
        isEmailValid: true,
        isPhoneNumberValid: true
      }
    },

    created() {
      this.getMarketplaceDisplays()
      this.referBrandRoute()
    },

    methods: {
      removeMarketplaceDisplay() {
        this.displayClicked(this.selectedDisplay, this.previousSelectedRow)
      },

      async getMarketplaceDisplays() {
        this.isBrandsLoading = true
        try {
          const marketplaceDisplays = await brandsMarketplaceAPI.getAllBrandDisplays()
          if (marketplaceDisplays[0].states.length) {
            this.showStateNav = true

            marketplaceDisplays[0].states.forEach((element) => {
              const keyName = Object.keys(element)[0]
              const state = {
                name: keyName,
                slides: []
              }

              for (let index = 0; index < element[keyName].length; index++) {
                const stateObject = element[keyName][index]
                const createdAt = new Date(stateObject.advertiser_created_at)
                const today = new Date()
                const threeMonthsAgo = subMonths(new Date(), 3)
                const monthResult = isWithinInterval(createdAt, {
                  start: threeMonthsAgo,
                  end: today
                })

                const newState = {
                  label: stateObject.display_name,
                  imgSrc: stateObject.image,
                  id: stateObject.id,
                  createdAt: stateObject.advertiser_created_at,
                }
                if (monthResult) newState["isNew"] = true
                state.slides.push(newState)
              }
              this.stateSlides.push(state)
            })
          }
          if (marketplaceDisplays[1].previously_sponsored.length) {
            this.showSponsoredNav = true
            this.createSlidesArray('sponsoredSlides', 'previously sponsored', marketplaceDisplays[1].previously_sponsored)
          }
          if (marketplaceDisplays[2].in_store.length) {
            this.showStoreNav = true
            this.createSlidesArray('storeSlides', 'in-store brands', marketplaceDisplays[2].in_store)
          }
          this.marketplaceDataLoaded = true
        } catch (error) {
          this.$store.dispatch('ADD_ERRORS', [ new UnexpectedError(error) ])
        } finally {
          this.isBrandsLoading = false
        }
      },

      createSlidesArray(dataProp, name, arr) {
        let state = {
          name: name,
          slides: []
        }

        arr.forEach((element) => {
          state.slides.push({
            label: element.display_name,
            imgSrc: element.image,
            id: element.id,
            createdAt: element.advertiser_created_at
          })
        })

        this[dataProp] = state
      },

      async getBrandDisplay(id) {
        this.isBrandsLoading = true
        try {
          this.$store.dispatch('DISMISS_ERRORS', { type: UnexpectedError })

          this.marketplaceDisplay = await brandsMarketplaceAPI.getBrandDisplay(id)
        } catch (error) {
          this.$store.dispatch('ADD_ERRORS', [ new UnexpectedError(error) ])
        } finally {
          this.isBrandsLoading = false
        }
      },

      scrollToElement(refName) {
        const topPortionHeight = document.querySelector('.top-portion-wrapper')?.clientHeight || 0
        const elementTop = this.$refs[refName]?.offsetTop
        const position = elementTop
          ? elementTop - topPortionHeight
          : topPortionHeight
        window.scrollTo({top: position, left: 0, behavior: 'smooth'})
      },

      closeModalAndResetFields() {
        this.clearFields()
        this.$store.dispatch('DISMISS_ERRORS', {type: UserError})
        this.showSimpleModal = false
      },

      async validateAndCreateRequest() {
        this.isFirstNameValid = !!this.brandContactFirstName?.length
        this.isLastNameValid = !!this.brandContactLastName?.length
        this.isBrandNameValid = !!this.brandName?.length
        this.validatePhoneNumber()
        this.validateEmail()

        const areContactNamesValid = this.isFirstNameValid && this.isLastNameValid
        const hasEmailOrPhone = !!this.brandEmail?.length || !!this.brandPhoneNumber?.length

        if (!areContactNamesValid || !hasEmailOrPhone || !this.isContactInfoValid) {
          if (!this.brandPhoneNumber?.length && !this.brandEmail?.length) {
            this.$store.dispatch('ADD_ERRORS', [new UserError(new Error('401'), 'phone number or email address required')])
          } else {
            this.$store.dispatch('DISMISS_ERRORS', {type: UserError})
          }
          return
        }

        try {
          this.$store.dispatch('DISMISSED_MODAL');
          this.$store.dispatch('DISMISS_ERRORS', { type: UnexpectedError })
          const response = await brandsMarketplaceAPI.postBrandReferral({
            brand_name: this.brandName,
            contact_name: this.brandContactFirstName,
            contact_last_name: this.brandContactLastName,
            phone_number: this.brandPhoneNumber,
            email: this.brandEmail
          })

          if (response.status === 201) {
            this.showSimpleModal = false
            this.clearFields()
          }
        } catch (error) {
          this.$store.dispatch('ADD_ERRORS', [ new UnexpectedError(error) ])
        }
      },

      clearFields() {
        this.brandName = ''
        this.brandContactFirstName = ''
        this.brandContactLastName = ''
        this.brandPhoneNumber = ''
        this.brandEmail = ''
        this.isFirstNameValid = true
        this.isLastNameValid = true
        this.isBrandNameValid = true
        this.isEmailValid = true
        this.isPhoneNumberValid = true
        this.isContactInfoValid = false
      },

      displayClicked(newDisplay, previousDisplayRowName) {
        if (this.selectedDisplay?.id === newDisplay.id && this.previousSelectedRow === previousDisplayRowName) {
          this.selectedDisplay = null
          this.previousSelectedRow = null
          this.marketplaceDisplay = null
          return false
        } else {
          this.selectedDisplay = newDisplay
          this.previousSelectedRow = previousDisplayRowName
        }

        this.getBrandDisplay(newDisplay.id)
      },

      validateField(name) {
        switch (name) {
          case 'contact_first_name':
            this.isFirstNameValid = !!this.brandContactFirstName?.length
            break;
          case 'contact_last_name':
            this.isLastNameValid = !!this.brandContactLastName?.length
            break;
          case 'phone_number':
            if (this.brandPhoneNumber?.length) {
              this.$store.dispatch('DISMISS_ERRORS', {type: UserError})
              const regex = /^\d{10}$/
              this.isPhoneNumberValid = regex.test(this.brandPhoneNumber)
            } else {
              this.isPhoneNumberValid = true
            }
            break;
          case 'email':
            if (this.brandEmail?.length) {
              this.$store.dispatch('DISMISS_ERRORS', {type: UserError})
              this.isEmailValid = EmailValidator.validate(this.brandEmail)
            } else {
              this.isEmailValid = true
            }
            break;
          default:
            this.isBrandNameValid = !!this.brandName?.length
        }
      },

      validateEmail() {
        if (this.brandEmail?.length) {
          this.isEmailValid = EmailValidator.validate(this.brandEmail)
          this.isContactInfoValid = this.isEmailValid
        }
      },

      validatePhoneNumber() {
        if (this.brandPhoneNumber?.length) {
          const regex = /^\d{10}$/
          this.isPhoneNumberValid = regex.test(this.brandPhoneNumber)
          this.isContactInfoValid = this.isPhoneNumberValid
        }
      },

      referBrandRoute(){
        if(this.$route.query?.refer_a_brand !== undefined) {
          this.showSimpleModal = true
        }
      },
    }
  }
</script>

<style scoped>
.header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
}

.links-wrapper a:not(:last-child) {
  margin-right: 50px;
}

.page-header {
  margin-bottom: 0;
  text-align: left;
  font-size: 1.5rem;
}

.top-portion-wrapper {
  background-color: white;
  z-index: 2;
  position: sticky;
  top: 0;
}

.error-banner {
  margin-bottom: 10px;
}

.carousel-title {
  margin-bottom: 10px;
  font-weight: 300;
  font-family: 'AileronLight', sans-serif;
}

.brand-carousels::v-deep img {
  border: 2px solid #128ff9;
  width: 150px;
  height: 150px;
  border-radius: 20px;
}

.brand-carousels::v-deep img.shadow-border {
  box-shadow: 0 0 0 5px #128ff9;
  border: 0;
}

.brand-carousels::v-deep .swiper-button-disabled {
  opacity: 0;
}

.states-list:not(.default-state) {
  height: 0;
  overflow: hidden;
}

.states-list.expanding-all-states {
  height: auto;
  overflow: auto;
}

.expand-states {
  color: #128ff9;
  cursor: pointer;
  background: white;
  font-size: 18px;
}

.expand-states.active {
  color: gray;
}

.display-label {
  font-size: 15px;
  margin: 5px auto;
  max-width: fit-content;
  word-break: break-word;
}

.new-badge {
  background-color: #128ff9;
  border-radius: 50%;
  color: white;
  padding: 8px 4px;
  right: -13px;
  top: -10px;
}

.refer-brand-modal::v-deep input {
  margin-bottom: 0;
}

.refer-brand-modal::v-deep label {
  margin-top: 1rem;
}

.refer-brand-modal::v-deep .refer-brand-error {
  border-color: red;
}

.refer-brand-modal::v-deep .error-banner {
  margin-top: 1rem;
}

/*------------------------------ STYLES FOR PREV, NEXT BUTTONS ON CAROUSEL ------------------------------*/
/* This exists because POs dont want the swiper buttons to be overlapped with the swiper */

.brands-marketplace-wrapper::v-deep .swiper-container {
  padding-right: 30px;
  padding-left: 30px;
}

.brands-marketplace-wrapper::v-deep .swiper-button-next,
.brands-marketplace-wrapper::v-deep .swiper-button-prev {
  top: 22px;
  z-index: 1 !important;
  height: 100%;
  background: white;
}

.brands-marketplace-wrapper::v-deep .swiper-button-next {
  right: 0;
}

.brands-marketplace-wrapper::v-deep .swiper-button-prev {
  left: 0;
}

/*------------------------------ MEDIA QUERIES ------------------------------*/
@media only screen and (max-width: 1023px) {
  .top-portion-wrapper {
    z-index: unset;
    position: unset;
    top: unset;
  }
  .header-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }
  .page-header {
    font-size: 24px;
  }
  .links-wrapper {
    display: flex;
    flex-direction: column;
  }
  .links-wrapper a {
    margin-bottom: 10px;
  }
  .brands-marketplace-navigation-wrapper {
    display: none;
  }
  .brand-carousels {
    margin: 0 20px;
  }
  .carousel-title {
    font-size: 22px;
  }
  .brand-carousels::v-deep img {
    width: 200px;
    height: 200px;
  }
}
</style>
